/**
 * Next.js ChunkLoadError
 * 배포 후 이전 버전의 chunk 파일을 로드하려고 할 때 발생하는 에러들
 * - 사용자가 이전 버전 페이지를 열어둔 상태에서 새 버전 배포됨
 * - 네트워크 불안정으로 chunk 로드 실패
 *
 * Solution: 청크 로드 실패 자동 새로고침(복구) 코드가 있는 앱에서는
 * Sentry에 보고할 필요가 없어 필터링한다.
 *
 * ⚠️ 단, quiz-app 에는 청크 에러 자동복구 코드가 없어(수동 새로고침 버튼뿐)
 * "자동 새로고침으로 처리" 전제가 성립하지 않는다. 게다가 EKS 카나리 배포(ZEP-591)
 * 중에는 buildId 스큐(구/신 빌드 혼입)의 1차 고장 신호가 바로 ChunkLoadError 라서,
 * quiz-app 은 아래 CHUNK_LOAD_ERROR_PATTERNS 를 createBeforeSend 의
 * `preservePatterns`(ignore 판정보다 우선하는 allowlist)로 지정해 관측한다.
 * ignorePatterns 에서 서브셋을 빼는 방식은 substring 매칭 특성상 상위 패턴
 * ('Failed to fetch' 등 NETWORK)이 청크 메시지를 삼켜 무력화되므로 쓰지 않는다.
 * (apps/quiz-app/sentry.client.config.ts 참고)
 *
 * Refs:
 * - https://sentry.io/answers/chunk-load-errors-javascript/
 * - https://augustinejoseph.medium.com/when-dynamic-imports-go-wrong-best-practices-for-recovery-a6990acead99
 *
 * Note: Hydration 에러는 실제 문제이므로 필터링하지 않음
 */

/**
 * 청크/동적 import 로드 실패 계열 패턴.
 * NEXTJS_ERROR_PATTERNS 에 포함되어 기본적으로 필터링되지만,
 * quiz-app 처럼 카나리 관측이 필요한 앱은 createBeforeSend 의
 * `preservePatterns` 로 지정해 드롭에서 제외(allowlist)할 수 있도록 분리.
 */
export const CHUNK_LOAD_ERROR_PATTERNS = [
  /**
   * ChunkLoadError - Webpack/Next.js chunk 로드 실패
   */
  'Loading chunk',
  'ChunkLoadError',
  /Loading chunk [\d]+ failed/,

  /**
   * Dynamic import 실패 - Vite, Webpack 등에서 발생
   */
  'Failed to fetch dynamically imported module',
  /Failed to fetch dynamically imported module.*\.js/,

  /**
   * CSS chunk 로드 실패
   */
  'Loading CSS chunk',
  /Loading CSS chunk [\d]+ failed/,
] as const;

export const NEXTJS_ERROR_PATTERNS = [
  ...CHUNK_LOAD_ERROR_PATTERNS,

  /**
   * Next.js Router - 같은 페이지로 hard navigation 시도
   * - 사용자가 현재 페이지 링크를 빠르게 여러 번 클릭
   * - router.push()가 동일한 경로로 중복 호출
   *
   * Solution: 무해한 에러이므로 필터링
   */
  'Invariant: attempted to hard navigate to the same',

  /**
   * Next.js Router - 라우트 변경 취소 (QUIZ-APP-4RY)
   * - 사용자가 페이지 이동 중 다른 링크 클릭
   * - 사용자가 페이지 이동 중 뒤로가기/앞으로가기
   * - 이전 라우트 변경이 완료되기 전에 새 라우트 변경 발생
   *
   * Solution: 정상적인 사용자 행동이므로 필터링
   */
  'routeChange aborted',

  /**
   * React 프로덕션 환경 에러
   * - 프로덕션 빌드에서 React 에러 메시지가 minify되어 "Minified React error #XXX"로 표시
   * - 에러 번호로 https://react.dev/errors/XXX 에서 원본 메시지 확인 가능하나,
   *   대부분 hydration 불일치(SSR/CSR 차이)나 concurrent mode 관련 에러
   * - 실제 사용자 영향이 적고 프로덕션 환경에서는 디버깅 정보가 부족
   * - web-app에서만 발생 (Next.js SSR 사용)
   *
   * Added: 2026-02-22 주간 Sentry 노이즈 리포트
   *
   * ⚠️ 필터 해제 검토 기준:
   * - React/Next.js 메이저 버전 업그레이드 후 hydration 에러가 급증하면 재검토
   * - 특정 페이지에서 집중 발생하면 해당 페이지의 SSR 로직 점검 필요
   *
   * Sentry Issues:
   * - https://zep-us.sentry.io/issues/WEB-APP-1FN (103 users, web-app)
   * - https://zep-us.sentry.io/issues/WEB-APP-1FT (96 users, web-app)
   * - https://zep-us.sentry.io/issues/WEB-APP-1GM (53 users, web-app)
   * - https://zep-us.sentry.io/issues/WEB-APP-1GC (33 users, web-app)
   */
  'Minified React error',
] as const;
